import { useCreditPricing, formatCredits } from '../../hooks/useCreditPricing';
import { useCredits } from '../../hooks/useCredits';

export interface CostItem {
  featureKey: string;
  qty: number;
  // Rótulo opcional para a unidade (ex: "posts (máx.)"). Default: unidade da tabela.
  label?: string;
  // Preço unitário já calculado pelo backend (ex.: estimativa do e-commerce por custo de
  // provedor). Quando vem, vale sobre a tabela de preços por ação.
  unitCredits?: number;
}

interface Props {
  items: CostItem[];
  // Sobrescreve o saldo (senão usa o hook useCredits).
  balance?: number;
  // Texto adicional (ex: "Você paga apenas pelos posts coletados").
  note?: string;
  className?: string;
  // Modo "por unidade": quantidade desconhecida antes de executar (ex: nº de anúncios).
  // Mostra "X créditos por {unidade}" em vez do total.
  perUnit?: boolean;
}

/**
 * Aviso de custo em créditos, para inserir ACIMA do botão de executar.
 * Fica vermelho quando o saldo é insuficiente.
 */
export function CreditCostNotice({ items, balance, note, className, perUnit }: Props) {
  const { getMeta, loading } = useCreditPricing();
  const { credits } = useCredits();

  if (loading) return null;

  const effectiveBalance = balance != null ? balance : (credits?.balance ?? 0);

  if (perUnit) {
    const parts = items
      .map(it => {
        const meta = getMeta(it.featureKey);
        if (!meta) return null;
        return `${formatCredits(meta.cost)} crédito${meta.cost !== 1 ? 's' : ''} por ${meta.unit}`;
      })
      .filter(Boolean);
    if (!parts.length) return null;
    return (
      <div className={`rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 ${className || ''}`}>
        <div className="flex items-start gap-2">
          <span className="text-sm leading-none">⚡</span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Custo: {parts.join(' · ')}</p>
            <p className="mt-1 text-[11px] opacity-80">
              Saldo atual: <span className="font-semibold">{formatCredits(effectiveBalance)}</span>
              {note ? ` · ${note}` : ''}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const lines = items
    .filter(it => it.qty > 0)
    .map(it => {
      const meta = getMeta(it.featureKey);
      const unitCost = it.unitCredits ?? meta?.cost ?? 0;
      const subtotal = Math.round(unitCost * it.qty * 10000) / 10000;
      return {
        subtotal,
        text: `${it.qty} ${it.label || meta?.unit || 'un.'} × ${formatCredits(unitCost)}`,
      };
    });

  const total = lines.reduce((sum, l) => sum + l.subtotal, 0);
  if (total <= 0) return null;

  const insufficient = effectiveBalance < total;

  return (
    <div
      className={`rounded-xl border px-3 py-2.5 text-xs ${
        insufficient
          ? 'border-red-200 bg-red-50 text-red-700'
          : 'border-amber-200 bg-amber-50 text-amber-800'
      } ${className || ''}`}
    >
      <div className="flex items-start gap-2">
        <span className="text-sm leading-none">⚡</span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            Esta ação consumirá{' '}
            <span className="font-black">{formatCredits(total)}</span>{' '}
            crédito{total !== 1 ? 's' : ''}
            {lines.length === 1 ? ` (${lines[0].text})` : ''}
          </p>
          {lines.length > 1 && (
            <ul className="mt-1 space-y-0.5 text-[11px] opacity-90">
              {lines.map((l, i) => (
                <li key={i}>• {l.text} = {formatCredits(l.subtotal)}</li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-[11px] opacity-80">
            Saldo atual: <span className="font-semibold">{formatCredits(effectiveBalance)}</span>
            {note ? ` · ${note}` : ''}
          </p>
          {insufficient && (
            <p className="mt-1 font-bold">
              Saldo insuficiente. Fale com o administrador para recarregar.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
